import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { pdf } from '@react-pdf/renderer';
import Button from '../Button/Button';
import CompteARebourd from '../CompteARebourd/CompteARebourd';
import ValidationPopUp from '../ValidationPopUp/ValidationPopUp';
import FormulaireAdministratif from '../FormulaireAdministratif/FormulaireAdministratif';
import OffrePDF from '../../pages/PDF/OffrePDF';
import CGVAccept from '../CGVAccept/CGVAccept';
import ModalArticles from '../ModalArticles/ModalArticles';
import ArticlesService from '../../services/articlesService';
import Coordonnees from '../Coordonnees/Coordonnees'; // NOUVEAU: Import du composant Coordonnees
import { ReactComponent as PdfIcon } from '../../assets/icons/pdf.svg';
import { useAuth } from '../../contexts/AuthContext';
import { isAdminRole, isClientRole, isPrestataireRole, isSignataireRole } from '../../utils/roleUtils';
import './OffreBC.css';
import { PrintButton } from '../PrintButton/PrintButton';
import { formatPrice, getStatusColor } from '../../utils/entityUtils';
import { getLotImage } from '../../utils/getLotImage';

// NOUVEAU: Interface Article pour TypeScript
interface Article {
  id: string;
  catalogueId?: string;
  code: string;
  nom: string;
  description: string;
  unite: string;
  quantite: number;
  prix: any;
  prixUnitaire?: number;
  totalHT: number;
  prixDegressifs?: any;
}

interface OffreBCProps {
  commande: any;
  rang: number;
  viewOnly?: boolean;
  // NOUVEAU: Props pour la gestion des CGV
  acceptCGV?: boolean;
  setAcceptCGV?: React.Dispatch<React.SetStateAction<boolean>>;
  printRef?: any;
}

const OffreBC: React.FC<OffreBCProps> = ({
  commande,
  rang = 1 | 2 | 3,
  viewOnly = false,
  acceptCGV = false,
  setAcceptCGV = () => { },
  printRef
}) => {
  const { user } = useAuth();
  const isClientView = isClientRole(user?.role || '');
  const isPrestataireView = isPrestataireRole(user?.role || '');

  // NOUVEAU: Fonction pour vérifier si la date est expirée
  const isDateExpired = (dateStr: string): boolean => {
    if (!dateStr) return false;

    const [datePart, timePart] = dateStr.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute, second] = timePart.split(':').map(Number);
    const targetDate = new Date(year, month - 1, day, hour, minute, second);
    const now = new Date();

    return now.getTime() >= targetDate.getTime();
  };

  // CORRECTION: Tous les useState doivent être déclarés en premier, sans conditions
  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false);
  const [isRefuseModalOpen, setIsRefuseModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isCGVModalOpen, setIsCGVModalOpen] = useState(false);
  const [readyToAcceptAfterCGV, setReadyToAcceptAfterCGV] = useState(false);
  const [readyToRefuseAfterCGV, setReadyToRefuseAfterCGV] = useState(false);
  const [readyToAcceptOperationAfterCGV, setReadyToAcceptOperationAfterCGV] = useState(false); // Prestataires
  const [readyToAcceptOffreAfterCGV, setReadyToAcceptOffreAfterCGV] = useState(false); // Acheteurs
  const [isAcceptOffreModalOpen, setIsAcceptOffreModalOpen] = useState(false);
  const [isRefuseOffreModalOpen, setIsRefuseOffreModalOpen] = useState(false);
  const [operationData, setOperationData] = useState<any>(null);
  const [loadingData, setLoadingData] = useState(true);

  // NOUVEAU: États pour la gestion des articles (panier inversé)
  const [isArticlesModalOpen, setIsArticlesModalOpen] = useState(false);
  const [existingArticles, setExistingArticles] = useState<Article[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Article[]>([]);
  const [loadingExistingArticles, setLoadingExistingArticles] = useState(false);
  const [isEnvoyerOffreModalOpen, setIsEnvoyerOffreModalOpen] = useState(false);

  // Ajouter un nouvel état pour suivre les suppressions en cours
  const [deletingArticleIds, setDeletingArticleIds] = useState<Set<string>>(new Set());

  // États pour la modal de notification au responsable
  const [isNotifyResponsableModalOpen, setIsNotifyResponsableModalOpen] = useState(false);
  const [responsables, setResponsables] = useState<any[]>([]);
  const [selectedResponsable, setSelectedResponsable] = useState<{ id: number, type: string } | null>(null);
  const [responsablesLoading, setResponsablesLoading] = useState(false);

  // NOUVEAU: Vérifier si une notification a déjà été envoyée à l'acheteur signataire
  // en regardant l'historique de l'opération pour un événement avec prefix "Notification" 
  // et type "BDC.Operation.{id}"
  const notificationInfo = useMemo(() => {
    const operationId = commande?.id;
    const historique = commande?.historique || [];

    if (!operationId || !Array.isArray(historique)) return { sent: false, labels: [] };

    // Chercher TOUS les événements de notification avec le bon format
    const notificationEvents = historique.filter((event: any) =>
      event.prefix === 'Notification' &&
      event.type === `BDC.Operation.${operationId}`
    );

    console.log('🔔 OffreBC - Vérification notifications déjà envoyées:', {
      operationId,
      historiqueLength: historique.length,
      notificationEvents,
      count: notificationEvents.length
    });

    if (notificationEvents.length > 0) {
      // Extraire les labels de toutes les notifications (triées par date décroissante - plus récentes en premier)
      const labels = notificationEvents
        .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .map((event: any) => event.label)
        .filter((label: string | null) => label !== null);
      return { sent: true, labels };
    }

    return { sent: false, labels: [] };
  }, [commande?.id, commande?.historique]);  // NOUVEAU: États pour la modal de saisie du numéro de référence interne
  const [isReferenceModalOpen, setIsReferenceModalOpen] = useState(false);
  const [referenceInterne, setReferenceInterne] = useState('');
  const [updatingReference, setUpdatingReference] = useState(false);

  // NOUVEAU: État pour gérer l'expiration (mis à jour dynamiquement)
  const [isOperationExpired, setIsOperationExpired] = useState(() => {
    const dateToCheck = operationData?.expire_at || commande?.expire_at;
    const statusId = operationData?.status?.id || commande?.status?.id;
    const source = operationData?.source || commande?.source;

    // CORRECTION: Exclure les BCI (source === 'BCI') de la logique d'expiration
    if (source === 'BCI') return false;

    const isExpiredByStatus = (source === 'BC' && statusId === 9);
    const isExpiredByDate = dateToCheck ? isDateExpired(dateToCheck) : false;

    return isExpiredByStatus || isExpiredByDate;
  });

  // CORRECTION: useEffect après tous les useState
  useEffect(() => {
    const fetchOperationData = async () => {
      try {
        setLoadingData(true);
        const token = localStorage.getItem('token');
        if (!token) throw new Error('Token manquant');

        let apiUrl = '';
        const currentPath = window.location.pathname;

        if (currentPath.includes('/operation-bc/')) {
          const operationId = currentPath.split('/')[2];
          apiUrl = `/bdc/operation/${operationId}`;
        } else if (currentPath.includes('/bon-commande/')) {
          const commandeId = currentPath.split('/')[2];
          apiUrl = `/bdc/commande/${commandeId}`;
        } else {
          setOperationData(commande);
          setLoadingData(false);
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}${apiUrl}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Erreur lors du chargement des données');
        }

        const result = await response.json();
        if (result.status === 'success' && result.data) {
          setOperationData(result.data);
        } else {
          throw new Error('Format de réponse invalide');
        }
      } catch (error) {
        console.error('Erreur lors du chargement:', error);
        setOperationData(commande);
      } finally {
        setLoadingData(false);
      }
    };

    fetchOperationData();
  }, [commande, rang]);

  // NOUVEAU: Vérifier l'expiration au chargement et quand les dates ou statuts changent
  useEffect(() => {
    const dateToCheck = operationData?.expire_at || commande?.expire_at;
    const statusId = operationData?.status?.id || commande?.status?.id;
    const source = operationData?.source || commande?.source;

    console.log('🔍 OffreBC - Vérification expiration:', {
      dateToCheck,
      statusId,
      source,
      isBCI: source === 'BCI',
      isExpiredByDate: dateToCheck ? isDateExpired(dateToCheck) : false,
      isExpiredByStatus: (source === 'BC' && statusId === 9),
      now: new Date().toISOString()
    });

    // CORRECTION: Exclure les BCI (source === 'BCI') de l'expiration
    if (source === 'BCI') {
      setIsOperationExpired(false);
      return;
    }

    const isExpiredByStatus = (source === 'BC' && statusId === 9);
    const isExpiredByDate = dateToCheck ? isDateExpired(dateToCheck) : false;

    setIsOperationExpired(isExpiredByStatus || isExpiredByDate);
  }, [operationData?.expire_at, commande?.expire_at, operationData?.status?.id, commande?.status?.id, operationData?.source, commande?.source]);

  // NOUVEAU: Écouter l'événement d'expiration du chrono
  useEffect(() => {
    const handleChronoExpired = () => {
      console.log('📢 OffreBC: Chrono expiré');
      setIsOperationExpired(true);
    };

    window.addEventListener('chronoExpired', handleChronoExpired);

    return () => {
      window.removeEventListener('chronoExpired', handleChronoExpired);
    };
  }, []);

  // NOUVEAU: Charger les articles existants depuis operationData
  useEffect(() => {
    if (!operationData?.articles || operationData.articles.length === 0) return;

    console.log('=== CHARGEMENT ARTICLES EXISTANTS ===');
    console.log('Articles depuis API:', operationData.articles);

    // Formatter les articles depuis l'API
    const articlesFormatted = operationData.articles.map((item: any) => {
      const catalogue = item.catalogue;
      const articleData = catalogue?.article;

      if (!catalogue || !articleData) return null;

      // Générer la référence
      const departementId = operationData.contrat?.lot_departement?.departement?.id || '00';
      const lotId = operationData.contrat?.lot_technique?.id || '000';
      const code = catalogue.code || '';
      const reference = `${departementId.toString().padStart(2, '0')} ${lotId.toString().padStart(3, '0')} ${code} ${rang}`;

      // Calculer le prix unitaire et total
      const quantite = parseInt(articleData.quantity) || 1;
      const prixUnitaire = parseFloat(articleData.prix_unitaire) || 0;
      const totalHT = parseFloat(articleData.prix_total) || (prixUnitaire * quantite);

      return {
        id: articleData.id?.toString() || catalogue.id?.toString() || '',
        catalogueId: catalogue.id?.toString() || '',
        code: reference,
        nom: catalogue.nom || '',
        description: catalogue.description || '',
        unite: catalogue.unite || 'Unité',
        quantite: quantite,
        prix: {
          prix_a: prixUnitaire,
          prix_b: prixUnitaire,
          prix_c: prixUnitaire
        },
        prixUnitaire: prixUnitaire,
        totalHT: totalHT,
        prixDegressifs: {
          palier1: {
            min: 1,
            max: catalogue.seuil_a || 5,
            prix: prixUnitaire
          },
          palier2: {
            min: (catalogue.seuil_a || 5) + 1,
            max: catalogue.seuil_b || 10,
            prix: prixUnitaire
          },
          palier3: {
            min: (catalogue.seuil_b || 10) + 1,
            prix: prixUnitaire
          }
        }
      };
    }).filter(Boolean);

    console.log('Articles formatés:', articlesFormatted);

    setExistingArticles(articlesFormatted);
    setSelectedProducts(articlesFormatted);
  }, [operationData, rang]);

  // NOUVEAU: Initialiser la référence interne avec la valeur existante (une seule fois au chargement)
  const [referenceInitialized, setReferenceInitialized] = useState(false);
  const [originalReference, setOriginalReference] = useState('');
  useEffect(() => {
    if (operationData && !referenceInitialized) {
      const existingReference = operationData?.reference || operationData?.formulaire?.referenceInterne || '';
      setReferenceInterne(existingReference);
      setOriginalReference(existingReference);
      setReferenceInitialized(true);
    }
  }, [operationData, referenceInitialized]);

  // Charger les articles existants pour les paniers inversés
  // useEffect(() => {
  //   const fetchExistingArticles = async () => {
  //     if (!isPrestataireView || !commande?.id) return;

  //     const isPanierInverse = commande.operation?.transferred === true || commande.transferred === true;
  //     if (!isPanierInverse) return;

  //     try {
  //       const token = localStorage.getItem('token');
  //       const response = await fetch(
  //         `${process.env.REACT_APP_API_URL}/bdc/commande/${commande.id}/catalogue`,
  //         {
  //           headers: {
  //             'Authorization': `Bearer ${token}`,
  //             'Content-Type': 'application/json'
  //           }
  //         }
  //       );

  //       if (response.ok) {
  //         const result = await response.json();
  //         if (result.status === 'success' && result.data) {
  //           const articles = result.data.map((item: any) => ({
  //             id: item.id,
  //             code: item.code || '',
  //             nom: item.nom || '',
  //             description: item.description || '',
  //             unite: item.unite || 'Unité',
  //             quantite: parseInt(item.quantite || 1),
  //             prixUnitaire: parseFloat(item.prix_unitaire || 0),
  //             totalHT: parseFloat(item.total_ht || 0)
  //           }));
  //           setExistingArticles(articles);
  //         }
  //       }
  //     } catch (error) {
  //       console.error('Erreur lors du chargement des articles:', error);
  //     }
  //   };

  //   fetchExistingArticles();
  // }, [commande, isPrestataireView]);

  // CORRECTION: Tous les useMemo après les useEffect
  const operationContext = useMemo(() => {
    if (!operationData) return null;

    const status = operationData.status?.nom;
    const statusId = operationData.status?.id;
    const isTransferred = isClientView ?
      operationData.transferred === true :
      operationData.operation?.transferred === true;

    const isPending =
      status === `En attente de l'acceptation du rang ${rang}` ||
      status === 'En consultation' ||
      status === 'En attente de votre acceptation' ||
      status === `En attente de votre acceptation du rang ${rang}` ||
      (isPrestataireView && statusId && (
        statusId === 2 || statusId === 3 || statusId === 4 || statusId === 5
      ));

    return {
      isTransferred,
      status,
      statusId,
      isAccepted: statusId === 7 || status === 'Accepté' || status === 'Validé',
      isRefused: statusId === 8 || status === 'Refusé' || status === 'Refuser',
      isExpired: statusId === 9 || status === 'Expiré' || status === 'Expirer',
      isPending: isPending
    };
  }, [operationData, isClientView, isPrestataireView, rang]);

  const articles = useMemo(() => {
    if (!operationData) return [];

    if (isPrestataireView) {
      return operationData.articles || [];
    }

    if (isClientView) {
      const commandeForRang = operationData.commandes?.find((cmd: any) => parseInt(cmd.rang) === rang);
      return commandeForRang?.articles || [];
    }

    return [];
  }, [operationData, rang, isPrestataireView, isClientView]);

  const formattedArticles = useMemo(() => {
    return articles.map((item: any, index: number) => {
      const catalogue = item.catalogue;
      const articleData = item.catalogue?.article;

      if (!catalogue || !articleData) {
        return {
          id: index,
          code: `Article ${index + 1}`,
          nom: 'Article non défini',
          description: '',
          unite: 'Unité',
          quantite: 1,
          prixUnitaire: 0,
          totalHT: 0
        };
      }

      const departementId = operationData?.operation?.lot_departement?.departement?.id || '00';
      const lotId = operationData?.operation?.lot_technique?.id || '000';
      const articleCode = catalogue.code || index;
      const reference = `${departementId.toString().padStart(2, '0')} ${lotId.toString().padStart(3, '0')} ${articleCode} ${rang}`;

      return {
        id: articleData.id || catalogue.id || index,
        code: reference,
        nom: catalogue.nom || `Article ${index + 1}`,
        description: catalogue.description || '',
        unite: catalogue.unite || 'Unité',
        quantite: parseInt(articleData.quantity || articleData.quantite || 1),
        prixUnitaire: parseFloat(articleData.prix_unitaire || articleData.prix || 0),
        totalHT: parseFloat(articleData.prix_total || articleData.totalHT || 0)
      };
    });
  }, [articles, operationData, rang]);

  const totalOffre = useMemo(() => {
    return formattedArticles.reduce((sum, article) => sum + article.totalHT, 0);
  }, [formattedArticles]);

  const acheteurRights = useMemo(() => {
    if (!isClientView) return { canAccept: false, canRefuse: false, isSignataire: false };

    const userRole = user?.role || '';
    const isSignataire = userRole === 'Acheteur signataire' ||
      userRole === 'Acheteur signataire admin' ||
      userRole === 'Admin';

    const status = operationData?.status?.nom;
    const canAct = status === 'En attente de votre acceptation' && isSignataire;

    return {
      canAccept: canAct,
      canRefuse: canAct,
      isSignataire: isSignataire
    };
  }, [isClientView, user, operationData]);

  // NOUVEAU: Déterminer si les CGV ont déjà été acceptées
  const cgvAlreadyAccepted = useMemo(() => {
    const isTransferred = operationData?.transferred === true;
    const statusNom = operationData?.status?.nom;

    // Si panier inversé et acheteur, les CGV ont été acceptées lors du lancement de la consultation (tab 1)
    if (isTransferred && isClientView) return true;

    // Si panier normal et status "À analyser", vérifier si un rang précédent a refusé ou expiré
    if (!isTransferred && statusNom === 'À analyser') {
      const rangPrecedent = rang - 1;
      const commandeRangPrecedent = rangPrecedent > 0
        ? operationData?.commandes?.find((cmd: any) => cmd.rang === rangPrecedent)
        : null;
      // CORRECTION: Inclure les commandes expirées (id_status === 9) en plus des refusées (id_status === 8)
      const rangPrecedentRefuseOuExpire = commandeRangPrecedent?.id_status === 8 || commandeRangPrecedent?.id_status === 9;

      // Si rang précédent a refusé ou expiré, les CGV ont été acceptées lors du lancement de la consultation
      return rangPrecedentRefuseOuExpire;
    }

    return false;
  }, [operationData, rang, isClientView]);

  // CORRECTION: Tous les useCallback après les useMemo
  const handleAccepterAvecCGV = useCallback(() => {
    // Si les CGV ont déjà été acceptées (rang précédent refusé), ne pas redemander
    if (cgvAlreadyAccepted || acceptCGV) {
      setIsAcceptModalOpen(true);
    } else {
      setReadyToAcceptOperationAfterCGV(true); // CORRECTION: Utiliser l'état pour les prestataires
      setIsCGVModalOpen(true);
    }
  }, [acceptCGV, cgvAlreadyAccepted]);

  // NOUVEAU: Ajouter la fonction manquante handleAccepterOffreAvecCGV
  const handleAccepterOffreAvecCGV = useCallback(async () => {
    // Sauvegarder si la référence a changé (même si vide)
    if (referenceInterne !== originalReference) {
      try {
        setUpdatingReference(true);
        const token = localStorage.getItem('token');
        if (!token) throw new Error('Token manquant');

        const operationId = operationData?.id;
        if (!operationId) throw new Error('ID de l\'opération non trouvé');

        const response = await fetch(`${process.env.REACT_APP_API_URL}/bdc/operation/${operationId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            reference: referenceInterne.trim()
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Erreur lors de la mise à jour de la référence');
        }

        const result = await response.json();

        // Mettre à jour les données locales et la référence originale
        if (result.data) {
          const newRef = referenceInterne.trim();
          setOriginalReference(newRef);
          setOperationData((prev: any) => ({
            ...prev,
            reference: newRef,
            formulaire: {
              ...prev?.formulaire,
              referenceInterne: newRef
            }
          }));
        }
      } catch (error) {
        console.error('Erreur lors de la mise à jour de la référence:', error);
        alert(error instanceof Error ? error.message : 'Erreur lors de la mise à jour de la référence');
        setUpdatingReference(false);
        return;
      } finally {
        setUpdatingReference(false);
      }
    }

    // Si les CGV ont déjà été acceptées (rang précédent refusé), ne pas redemander
    if (cgvAlreadyAccepted || acceptCGV) {
      setIsAcceptOffreModalOpen(true);
    } else {
      setReadyToAcceptOffreAfterCGV(true); // CORRECTION: Utiliser l'état pour les acheteurs
      setIsCGVModalOpen(true);
    }
  }, [acceptCGV, cgvAlreadyAccepted, operationData, referenceInterne, originalReference]);

  // NOUVEAU: Fonction pour continuer l'acceptation après la modal de référence
  const proceedWithAcceptation = useCallback(() => {
    if (cgvAlreadyAccepted || acceptCGV) {
      setIsAcceptOffreModalOpen(true);
    } else {
      setReadyToAcceptOffreAfterCGV(true);
      setIsCGVModalOpen(true);
    }
  }, [acceptCGV, cgvAlreadyAccepted]);

  // NOUVEAU: Fonction pour mettre à jour la référence interne et continuer
  const handleUpdateReferenceAndAccept = useCallback(async () => {
    if (!referenceInterne.trim()) {
      // Si pas de référence saisie, continuer directement
      setIsReferenceModalOpen(false);
      proceedWithAcceptation();
      return;
    }

    try {
      setUpdatingReference(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');

      const operationId = operationData?.id;
      if (!operationId) throw new Error('ID de l\'opération non trouvé');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/bdc/operation/${operationId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reference: referenceInterne.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors de la mise à jour de la référence');
      }

      const result = await response.json();

      // Mettre à jour les données locales avec la nouvelle référence
      if (result.data) {
        setOperationData((prev: any) => ({
          ...prev,
          reference: referenceInterne.trim(),
          formulaire: {
            ...prev?.formulaire,
            referenceInterne: referenceInterne.trim()
          }
        }));
      }

      setIsReferenceModalOpen(false);
      proceedWithAcceptation();
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la référence:', error);
      alert(error instanceof Error ? error.message : 'Erreur lors de la mise à jour de la référence');
    } finally {
      setUpdatingReference(false);
    }
  }, [referenceInterne, operationData, proceedWithAcceptation]);

  // NOUVEAU: Fonction pour ignorer la saisie de référence et continuer
  const handleSkipReference = useCallback(() => {
    setIsReferenceModalOpen(false);
    proceedWithAcceptation();
  }, [proceedWithAcceptation]);

  // NOUVEAU: Ajouter les fonctions manquantes handleAcceptOffre et handleRefuseOffre
  const handleAcceptOffre = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');

      // NOUVEAU: Détecter si on est côté prestataire ou acheteur
      // Prestataire: operationData est la commande (pas de tableau commandes)
      // Acheteur: operationData est l'opération (avec tableau commandes)
      const isPrestataire = !operationData?.commandes && operationData?.id_operation;

      let url, commandeId;

      if (isPrestataire) {
        // Côté prestataire: route /bdc/commande/<id_commande>/accept
        commandeId = operationData?.id;
        if (!commandeId) {
          throw new Error('ID de la commande non trouvé');
        }
        url = `${process.env.REACT_APP_API_URL}/bdc/commande/${commandeId}/accept`;
      } else {
        // Côté acheteur: route /bdc/operation/<id_operation>/accept/<id_commande>
        const operationId = operationData?.id;
        const commandeActuelle = operationData?.commandes?.find((cmd: any) => cmd.rang === rang);
        commandeId = commandeActuelle?.id;

        if (!operationId) {
          throw new Error('ID de l\'opération non trouvé');
        }
        if (!commandeId) {
          throw new Error(`ID de la commande du rang ${rang} non trouvé`);
        }
        url = `${process.env.REACT_APP_API_URL}/bdc/operation/${operationId}/accept/${commandeId}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers:
        {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors de l\'acceptation de l\'offre');
      }

      alert('Offre acceptée avec succès !');
      window.location.reload();
    } catch (error) {
      console.error('Erreur:', error);
      alert(error instanceof Error ? error.message : 'Erreur lors de l\'acceptation');
    } finally {
      setLoading(false);
    }
  }, [operationData]);

  const handleRefuseOffre = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');

      // NOUVEAU: Détecter si on est côté prestataire ou acheteur
      const isPrestataire = !operationData?.commandes && operationData?.id_operation;

      let url, commandeId;

      if (isPrestataire) {
        // Côté prestataire: route /bdc/commande/<id_commande>/refuse
        commandeId = operationData?.id;
        if (!commandeId) {
          throw new Error('ID de la commande non trouvé');
        }
        url = `${process.env.REACT_APP_API_URL}/bdc/commande/${commandeId}/refuse`;
      } else {
        // Côté acheteur: route /bdc/operation/<id_operation>/refuse/<id_commande>
        const operationId = operationData?.id;
        const commandeActuelle = operationData?.commandes?.find((cmd: any) => cmd.rang === rang);
        commandeId = commandeActuelle?.id;

        if (!operationId) {
          throw new Error('ID de l\'opération non trouvé');
        }
        if (!commandeId) {
          throw new Error(`ID de la commande du rang ${rang} non trouvé`);
        }
        url = `${process.env.REACT_APP_API_URL}/bdc/operation/${operationId}/refuse/${commandeId}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors du refus de l\'offre');
      }

      if (rang < 3) {
        alert(`Offre refusée.`);
      } else {
        alert('Offre refusée.');
      }

      window.location.reload();
    } catch (error) {
      console.error('Erreur:', error);
      alert(error instanceof Error ? error.message : 'Erreur lors du refus');
    } finally {
      setLoading(false);
    }
  }, [operationData, rang]);

  const handleAcceptOperation = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');

      // NOUVEAU: Détecter si on est côté prestataire ou acheteur
      const isPrestataire = !operationData?.commandes && operationData?.id_operation;

      let url, commandeId;

      if (isPrestataire) {
        // Côté prestataire: route /bdc/commande/<id_commande>/accept
        commandeId = operationData?.id;
        if (!commandeId) {
          throw new Error('ID de la commande non trouvé dans les données API');
        }
        url = `${process.env.REACT_APP_API_URL}/bdc/commande/${commandeId}/accept`;
      } else {
        // Côté acheteur: route /bdc/operation/<id_operation>/accept/<id_commande>
        const operationId = operationData?.id;
        const commandeActuelle = operationData?.commandes?.find((cmd: any) => cmd.rang === rang);
        commandeId = commandeActuelle?.id;

        if (!operationId) {
          throw new Error('ID de l\'opération non trouvé dans les données API');
        }
        if (!commandeId) {
          throw new Error(`ID de la commande du rang ${rang} non trouvé`);
        }
        url = `${process.env.REACT_APP_API_URL}/bdc/operation/${operationId}/accept/${commandeId}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors de l\'acceptation');
      }

      alert('Opération acceptée avec succès !');
      window.location.reload();
    } catch (error) {
      console.error('Erreur:', error);
      alert(error instanceof Error ? error.message : 'Erreur lors de l\'acceptation');
    } finally {
      setLoading(false);
    }
  }, [operationData]);

  const handleRefuseOperation = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');

      // NOUVEAU: Détecter si on est côté prestataire ou acheteur
      const isPrestataire = !operationData?.commandes && operationData?.id_operation;

      let url, commandeId;

      if (isPrestataire) {
        // Côté prestataire: route /bdc/commande/<id_commande>/refuse
        commandeId = operationData?.id;
        if (!commandeId) {
          throw new Error('ID de la commande non trouvé dans les données API');
        }
        url = `${process.env.REACT_APP_API_URL}/bdc/commande/${commandeId}/refuse`;
      } else {
        // Côté acheteur: route /bdc/operation/<id_operation>/refuse/<id_commande>
        const operationId = operationData?.id;
        const commandeActuelle = operationData?.commandes?.find((cmd: any) => cmd.rang === rang);
        commandeId = commandeActuelle?.id;

        if (!operationId) {
          throw new Error('ID de l\'opération non trouvé dans les données API');
        }
        if (!commandeId) {
          throw new Error(`ID de la commande du rang ${rang} non trouvé`);
        }
        url = `${process.env.REACT_APP_API_URL}/bdc/operation/${operationId}/refuse/${commandeId}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors du refus');
      }

      if (rang < 3) {
        alert(`Opération refusée.`);
      } else {
        alert('Opération refusée par tous les prestataires.');
      }

      window.location.reload();
    } catch (error) {
      console.error('Erreur:', error);
      alert(error instanceof Error ? error.message : 'Erreur lors du refus');
    } finally {
      setLoading(false);
    }
  }, [operationData, rang]);

  const generatePDF = useCallback(async () => {
    try {
      if (!operationData || !user) {
        console.error('Données manquantes pour générer le PDF');
        alert('Impossible de générer le PDF : données manquantes');
        return;
      }

      const pdfData = {
        ...operationData,
        formulaire: operationData.formulaire || {}
      };

      const blob = await pdf(<OffrePDF commande={pdfData} user={user} />).toBlob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `offre-${operationData.numero || operationData.id || 'document'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('❌ Erreur lors de la génération du PDF:', error);
      alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
    }
  }, [operationData, user]);

  const handleDownloadBonCommande = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');

      const operationId = operationData?.id;
      if (!operationId) throw new Error("ID de l'opération non trouvé");

      const response = await fetch(`${process.env.REACT_APP_API_URL}/documents/signed-contrat`, {
        method: 'POST', // maintenant en POST pour suivre le modèle Signed
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/pdf',
        },
        body: JSON.stringify({
          external_id: operationData.numero.toString(),
          external_type: 'operation',
          source: 'BC', // préciser le type pour la route backend
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erreur lors du téléchargement du bon de commande');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Retirer le P si le numéro commence par P
      const rawNumero = operationData?.numero ?? operationData?.id?.toString() ?? 'document';
      const numeroSansP = rawNumero.startsWith('P') ? rawNumero.substring(1) : rawNumero;

      const annee = process.env.REACT_APP_ANNEE_EDITION || '25';
      link.download = `${annee}${numeroSansP} (signed).pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('❌ Erreur lors du téléchargement du bon de commande:', error);
      alert('Erreur lors du téléchargement du bon de commande. Veuillez réessayer.');
    }
  }, [operationData]);

  // NOUVEAU: Déterminer l'état spécifique de l'offre pour ce rang
  const offreState = useMemo(() => {
    if (!operationData) {
      return { showBonCommande: false, showRefusMessage: false, refusInfo: null };
    }

    // CORRECTION: Pour les prestataires, utiliser le statut global de l'opération
    if (isPrestataireView) {
      const status = operationData.status?.nom;
      const statusId = operationData.status?.id;

      console.log('=== DEBUG OFFRE STATE PRESTATAIRE ===');
      console.log('Status:', status);
      console.log('Status ID:', statusId);
      console.log('Type compte:', operationData?.type_compte);

      const isAccepted = status === 'Accepté' || status === 'Valider' || status === 'Valide';
      const isRefused = statusId === 8 || status === 'Refusé';

      // Détecter qui a refusé en fonction du type_compte
      // type_compte = 'acheteur' => l'acheteur a créé/modifié en dernier (donc il a refusé)
      // type_compte = 'prestataire' => le prestataire a créé/modifié en dernier (donc il a refusé)
      const refusedBy = operationData?.type_compte === 'acheteur' ? 'acheteur' : 'prestataire';
      const acheteurNom = operationData?.operation?.organisme?.nom || operationData?.operation?.acheteur?.nom || 'Acheteur';
      const prestataireNom = operationData?.contrat?.entite?.nom || operationData?.prestataire?.nom || 'Prestataire';

      console.log('Refusé par:', refusedBy);
      console.log('Nom acheteur:', acheteurNom);
      console.log('Nom prestataire:', prestataireNom);

      return {
        showBonCommande: isAccepted,
        showRefusMessage: isRefused,
        refusInfo: isRefused ? {
          date: operationData.updated_at,
          refusedBy: refusedBy,
          acheteurNom: acheteurNom,
          prestataireNom: prestataireNom,
          rang: rang
        } : null
      };
    }

    // CORRECTION: Pour les acheteurs, utiliser la logique par rang existante
    if (isClientView) {
      const commandes = operationData.commandes || [];
      const commandeForRang = commandes.find((cmd: any) => parseInt(cmd.rang) === rang);

      console.log('=== DEBUG OFFRE STATE ACHETEUR ===');
      console.log('Rang:', rang);
      console.log('Commande pour rang:', commandeForRang);
      console.log('Status ID commande:', commandeForRang?.id_status);
      console.log('Type compte:', commandeForRang?.type_compte);
      console.log('Historique:', commandeForRang?.historique);

      if (!commandeForRang) {
        return { showBonCommande: false, showRefusMessage: false, refusInfo: null };
      }

      const statusId = commandeForRang.id_status;
      const isAccepted = statusId === 7 || status === 'Accepté' || status === 'Valider' || status === 'Valide'; // Accepté
      const isRefused = statusId === 8 || status === 'Refusé';  // Refusé
      const isExpired = statusId === 9 || status === 'Expiré' || status === 'Expirer';  // Expiré
      const isRefusedOrExpired = isRefused || isExpired;

      // CORRECTION: Détecter qui a refusé en vérifiant l'historique
      let refusedBy = 'prestataire'; // Par défaut

      // Chercher dans l'historique l'événement de refus
      const refusEvent = commandeForRang?.historique?.find((h: any) =>
        h.prefix?.toLowerCase().includes('refus') ||
        h.message?.toLowerCase().includes('refus')
      );

      if (refusEvent) {
        // Si l'événement contient "acheteur" ou si le compte est de type acheteur
        if (refusEvent.message?.toLowerCase().includes('acheteur') ||
          refusEvent.prefix?.toLowerCase().includes('acheteur') ||
          refusEvent.type_compte === 'acheteur') {
          refusedBy = 'acheteur';
        }
      } else if (commandeForRang?.type_compte === 'acheteur') {
        // Fallback sur type_compte si pas d'historique
        refusedBy = 'acheteur';
      }

      console.log('Refusé par (détecté):', refusedBy);
      console.log('Événement de refus trouvé:', refusEvent);

      const acheteurNom = operationData?.organisme?.nom || operationData?.acheteur?.nom || 'Acheteur';
      const prestataireNom = commandeForRang.contrat?.entite?.nom || 'Prestataire';

      console.log('Refusé par:', refusedBy);
      console.log('Nom acheteur:', acheteurNom);
      console.log('Nom prestataire:', prestataireNom);

      return {
        showBonCommande: isAccepted,
        showRefusMessage: isRefusedOrExpired,
        refusInfo: isRefusedOrExpired ? {
          date: commandeForRang.updated_at,
          refusedBy: refusedBy,
          acheteurNom: acheteurNom,
          prestataireNom: prestataireNom,
          rang: rang,
          isExpired: isExpired
        } : null
      };
    }

    return { showBonCommande: false, showRefusMessage: false, refusInfo: null };
  }, [operationData, rang, isClientView, isPrestataireView]);

  // NOUVEAU: Calculer si existingArticles contient des articles
  const hasExistingArticles = useMemo(() => {
    return existingArticles.length > 0 || selectedProducts.length > 0;
  }, [existingArticles, selectedProducts]);

  // NOUVEAU: Fonction pour ajouter des articles à la commande (callback pour ModalArticles)
  const handleAddToCommand = useCallback((articles: Article[]) => {
    console.log('=== handleAddToCommand OffreBC ===');
    console.log('Articles reçus:', articles);

    setExistingArticles(articles);
    setSelectedProducts(articles);
    setIsArticlesModalOpen(false);
  }, []);

  // NOUVEAU: Fonction pour supprimer un article existant
  const handleRemoveExistingArticle = useCallback(async (catalogueId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) return;

    // Vérifier si une suppression est déjà en cours pour cet article
    if (deletingArticleIds.has(catalogueId)) return;

    try {
      // Ajouter l'ID à la liste des suppressions en cours
      setDeletingArticleIds(prev => new Set(prev).add(catalogueId));

      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');

      const commandeId = operationData?.id;
      if (!commandeId) throw new Error('ID de commande manquant');

      const articleToRemove = existingArticles.find(article =>
        article.catalogueId === catalogueId || article.id === catalogueId
      );

      if (!articleToRemove) {
        console.error('Article non trouvé:', catalogueId);
        return;
      }

      console.log('=== Suppression article ===');
      console.log('Article à supprimer:', articleToRemove);
      console.log('Commande ID:', commandeId);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/bdc/commande/${commandeId}/catalogue/${catalogueId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors de la suppression');
      }

      // Mettre à jour l'état local
      const updatedArticles = existingArticles.filter(article =>
        article.catalogueId !== catalogueId && article.id !== catalogueId
      );

      setExistingArticles(updatedArticles);
      setSelectedProducts(updatedArticles);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert(error instanceof Error ? error.message : 'Erreur lors de la suppression de l\'article');
    } finally {
      // Retirer l'ID de la liste des suppressions en cours
      setDeletingArticleIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(catalogueId);
        return newSet;
      });
    }
  }, [operationData, existingArticles, deletingArticleIds]);

  // NOUVEAU: Fonction pour envoyer l'offre (transférer à l'acheteur pour validation)
  const handleEnvoyerOffre = useCallback(async () => {
    try {
      if (existingArticles.length === 0) {
        alert('Vous devez sélectionner au moins un article avant d\'envoyer votre offre.');
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');

      const commandeId = operationData?.id;
      if (!commandeId) throw new Error('ID de commande manquant');

      console.log('=== Transfert de l\'offre à l\'acheteur ===');
      console.log('Commande ID:', commandeId);
      console.log('Nombre d\'articles:', existingArticles.length);

      // CORRECTION: Utiliser transfertAcheteur au lieu de accepter
      // Le prestataire transfère son offre à l'acheteur qui devra la valider
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/bdc/commande/${commandeId}/transfertAcheteur`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors de l\'envoi de l\'offre');
      }

      const result = await response.json();
      console.log('Offre transférée avec succès:', result);

      alert('Votre offre a été envoyée avec succès à l\'acheteur pour validation !');

      // Fermer la modal de confirmation
      setIsEnvoyerOffreModalOpen(false);

      // ✅ Recharger la page sur l'onglet Offre (tab=2) pour actualiser les données
      window.location.href = `${window.location.pathname}?tab=2`;
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      alert(error instanceof Error ? error.message : 'Erreur lors de l\'envoi de l\'offre');
    }
  }, [operationData, existingArticles]);

  // CORRECTION: Vérification de chargement APRÈS tous les hooks
  if (loadingData || !operationContext) {
    return <div className="loading-indicator">Chargement de l'offre...</div>;
  }

  // Charger la liste des responsables
  const loadResponsables = async () => {
    setResponsablesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/acheteurs/signataires`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log('Responsables chargés:', result);
        setResponsables(result.data || result.signataires || []);
      } else {
        console.error('Erreur lors du chargement des responsables:', response.status);
        setResponsables([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des responsables:', error);
      setResponsables([]);
    } finally {
      setResponsablesLoading(false);
    }
  };

  const handleNotifyResponsable = async () => {
    // Sauvegarder si la référence a changé (même si vide)
    if (referenceInterne !== originalReference) {
      try {
        setUpdatingReference(true);
        const token = localStorage.getItem('token');
        if (!token) throw new Error('Token manquant');

        const operationId = operationData?.id;
        if (!operationId) throw new Error('ID de l\'opération non trouvé');

        const response = await fetch(`${process.env.REACT_APP_API_URL}/bdc/operation/${operationId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            reference: referenceInterne.trim()
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Erreur lors de la mise à jour de la référence');
        }

        const result = await response.json();

        // Mettre à jour les données locales et la référence originale
        if (result.data) {
          const newRef = referenceInterne.trim();
          setOriginalReference(newRef);
          setOperationData((prev: any) => ({
            ...prev,
            reference: newRef,
            formulaire: {
              ...prev?.formulaire,
              referenceInterne: newRef
            }
          }));
        }
      } catch (error) {
        console.error('Erreur lors de la mise à jour de la référence:', error);
        alert(error instanceof Error ? error.message : 'Erreur lors de la mise à jour de la référence');
        setUpdatingReference(false);
        return;
      } finally {
        setUpdatingReference(false);
      }
    }

    // Ouvrir la modal de sélection
    setIsNotifyResponsableModalOpen(true);
    loadResponsables();
  };

  const handleConfirmNotifyResponsable = async () => {
    try {
      if (!selectedResponsable) {
        alert('Veuillez sélectionner un responsable');
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error("Token d'authentification manquant");
      }

      // Récupérer l'ID de l'opération depuis l'URL
      const currentPath = window.location.pathname;
      const operationId = currentPath.split('/')[2];

      if (!operationId) {
        throw new Error("ID de l'opération non trouvé");
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/bdc/operation/${operationId}/notification-responsable`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            responsable_id: selectedResponsable.id,
            responsable_type: selectedResponsable.type
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();
      console.log('Demande envoyée avec succès:', result);

      alert(`Demande envoyée à ${result.responsable?.prenom} ${result.responsable?.nom} avec succès !`);
      setIsNotifyResponsableModalOpen(false);
      setSelectedResponsable(null);

    } catch (error) {
      console.error('Erreur lors de l\'envoi de la demande au responsable:', error);
      alert(`Erreur lors de l'envoi de la demande au responsable: ${(error as Error).message}`);
    }
  };

  const getDisplayStatus = () => {
    // CORRECTION: Utiliser operationData (données fraîches de l'API) au lieu de commande (props périmées)
    const dataSource = operationData || commande;

    if (!dataSource?.status) return 'Statut inconnu';

    const statusName = dataSource.status.nom;
    const statusId = dataSource.status.id;

    console.log('🔍 getDisplayStatus:', {
      isPrestataireView,
      isClientView,
      statusName,
      statusId,
      dataSource: operationData ? 'operationData (fresh)' : 'commande (props)'
    });

    // Pour les prestataires BC, adapter l'affichage selon le contexte
    if (isPrestataireView) {
      const rang = getRangPrestataire();

      // Si c'est "En attente de l'acceptation du rang X" et que c'est le rang du prestataire connecté
      if (statusName === `En attente de l'acceptation du rang ${rang}`) {
        return 'À accepter';
      }
      // Sinon, utiliser le nom tel quel
      return statusName;
    }

    // Pour les acheteurs, utiliser le nom tel quel
    return statusName;
  };

  // CORRECTION: Déterminer le rang du prestataire depuis les données API
  const getRangPrestataire = () => {
    if (!commande || !isPrestataireView) return 1;
    return commande.rang || 1;
  };

  // CORRECTION: Fonction pour formater le sous-titre
  const getSousTitre = () => {
    const numero = commande?.operation?.numero || commande?.numero;
    const rang = getRangPrestataire();

    if (isPrestataireView) {
      return `Suivi de la Commande BC n° ${numero} - Rang ${rang}`;
    }
    return `Suivi de la Commande BC n° ${numero}`;
  };

  // CORRECTION: Fonction pour formater le subtitle (lot technique + géographique)
  const getSubtitle = () => {
    const lotTechnique = commande?.operation?.lot_technique || commande?.lot_technique;
    const departement = commande?.operation?.lot_departement?.departement || commande?.lot_departement?.departement;

    if (lotTechnique && departement) {
      return `${lotTechnique.id} - ${lotTechnique.nom}, ${departement.id} - ${departement.nom}`;
    }
    return 'Informations de lot non disponibles';
  };

  return (
    <>
      <div className="consultation-bc">
        {/* NOUVEAU: Affichage du message d'expiration */}
        {isOperationExpired && (
          <div className="expired-notice" style={{
            backgroundColor: '#fee',
            border: '2px solid #c33',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            color: '#c33',
            fontWeight: 'bold',
            textAlign: 'center'
          }}>
            Cette commande est expirée. Vous ne pouvez plus modifier votre offre ni effectuer d'actions.
          </div>
        )}

        {/* RÉUTILISATION: Section Descriptif Administratif (même structure que ConsultationBC) */}
        <section>
          <h3 className="section-header">Descriptif Administratif</h3>
          <div className="section-content">
            <FormulaireAdministratif
              formData={{
                ...operationData?.formulaire || {},
                nomOperation: operationData?.nom || operationData?.formulaire?.nomOperation || '',
                referenceInterne: operationData?.reference || operationData?.formulaire?.referenceInterne || '',
                adresse: operationData?.adresse || operationData?.formulaire?.adresse || '',
                codePostal: operationData?.code_postal || operationData?.formulaire?.codePostal || '',
                ville: operationData?.ville || operationData?.formulaire?.ville || '',
                coordonneesMaitriseDOuvrage: operationData?.facturation || operationData?.formulaire?.coordonneesMaitriseDOuvrage || '',
                coordonneesMaitrise: operationData?.coordonnees_maitrise_ouvrage || operationData?.formulaire?.coordonneesMaitrise || '',
                delaisConsultation: operationData?.consultation || 10,
                delaisFinOperation: operationData?.delai_execution || operationData?.delais || operationData?.formulaire?.delaisFinOperation || 30,
                visitePreavis: operationData?.visite === true || operationData?.visite === 1 || operationData?.visite === "1",
                // ✅ CORRECTION: Mapper condition_visite de l'API
                conditionVisite: operationData?.condition_visite || operationData?.formulaire?.conditionVisite || '',
                dateDebutOperation: operationData?.date_debut_operation || operationData?.formulaire?.dateDebutOperation || '',
                dateFinOperation: operationData?.date_fin_operation || operationData?.formulaire?.dateFinOperation || ''
              }}
              viewMode={true} // Toujours en lecture seule dans l'offre
              hideBesoin={true} // Masquer le besoin ici, il sera affiché dans la section technique
              errors={{}}
              commande={operationData}
              operation={operationData?.operation || operationData}
            />
          </div>
        </section>

        {/* RÉUTILISATION: Section Descriptif Technique (même structure que ConsultationBC) */}
        <section>
          <h3 className="section-header">Descriptif Technique</h3>
          <div className="section-content">
            <div className="form-group">
              <label>Cahier des charges <span className="desc-precision">(Les liens URL vers des pièces graphiques ou des documents complémentaires peuvent être proposés par l’acheteur à titre d’information, mais seules les informations écrites ci-dessous sont contractuelles. Par ailleurs, toutes précisions administratives ou juridiques de nature à contrevenir à l’accord-cadre général sans dérogations prévues au contrat est a considérer comme inexistantes dans ce descriptif)</span></label>
              <div className="input-inactif definition-besoin">
                {operationData?.besoin || operationData?.operation?.besoin || operationData?.formulaire?.besoin || 'Non défini'}
              </div>
            </div>
          </div>
        </section>

        {/* Section Descriptif Financier */}
        <section>
          <h3 className="section-header">Descriptif Financier</h3>
          <div className="section-content">
            {/* NOUVEAU: Pour les prestataires en mode "À chiffrer" (panier inversé), afficher l'interface de sélection d'articles */}
            {isPrestataireView && operationContext?.status === 'À chiffrer' ? (
              <div className="prestataire-fill-articles">
                {/* Bandeau d'instructions */}
                <div className="prestataire-instructions">
                  <h4>Définir les articles pour cette opération</h4>
                  <p>L'acheteur vous demande de définir les articles nécessaires pour cette opération. Sélectionnez les articles appropriés dans le catalogue.</p>
                </div>

                {/* Si des articles existent déjà, afficher le tableau */}
                {hasExistingArticles ? (
                  <div className="articles-table-container">
                    <div className="articles-table-wrapper">
                      <table>
                        <thead>
                          <tr>
                            <th>N° Réf</th>
                            <th>Description de la prestation</th>
                            <th>Unité</th>
                            <th>Quantité</th>
                            <th>Prix unitaire HT</th>
                            <th>Total HT</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {existingArticles.map((article, index) => (
                            <tr key={article.id || index}>
                              <td className="article-number">{article.code}</td>
                              <td className="article-description">
                                <div className="article-name">{article.nom}</div>
                              </td>
                              <td className="article-unit">{article.unite}</td>
                              <td className="article-quantity">
                                <div className="quantity-cell">{article.quantite}</div>
                              </td>
                              <td className="article-price price-cell">
                                {formatPrice(ArticlesService.getPrixByQuantity(article, article.quantite), 4)}
                              </td>
                              <td className="article-total total-cell">
                                {formatPrice(article.totalHT, 4)}
                              </td>
                              <td className="article-actions">
                                <button
                                  className="delete-button"
                                  onClick={() => handleRemoveExistingArticle(article.catalogueId || article.id)}
                                  disabled={deletingArticleIds.has(article.catalogueId || article.id)}
                                  type="button"
                                >×</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Bouton pour ajouter/modifier des articles - aligné à droite */}
                    {!isOperationExpired && (
                      <div style={{ textAlign: 'right', padding: '0 1rem 1rem 1rem' }}>
                        <button
                          className="add-article-button"
                          type="button"
                          onClick={() => setIsArticlesModalOpen(true)}
                        >Ajouter ou modifier un article</button>
                      </div>
                    )}

                    {/* Section total */}
                    <div className="articles-actions">
                      <div className="total-section">
                        <p>Total HT de votre offre : {formatPrice(existingArticles.reduce((sum, article) => sum + article.totalHT, 0), 4)}</p>
                      </div>
                    </div>

                    {/* Bouton pour envoyer l'offre */}
                    {!isOperationExpired && (
                      <div className="action-buttons-group" style={{ marginTop: '20px', textAlign: 'center' }}>
                        <Button
                          value="Envoyer l'offre à l'acheteur"
                          variant="primary"
                          size="medium"
                          onClick={() => setIsEnvoyerOffreModalOpen(true)}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  // Aucun article sélectionné : afficher le bouton de sélection ou message si expiré
                  <div className="financial-choice">
                    {!isOperationExpired ? (
                      <div className="choice-buttons">
                        <Button
                          value="Sélectionner des articles du catalogue"
                          variant="primary"
                          size="medium"
                          onClick={() => setIsArticlesModalOpen(true)}
                        />
                      </div>
                    ) : (
                      <div className="expired-message" style={{
                        padding: '20px',
                        textAlign: 'center',
                        color: '#c33',
                        backgroundColor: '#fee',
                        borderRadius: '8px',
                        border: '1px solid #c33'
                      }}>
                        Cette commande est expirée. Vous ne pouvez plus sélectionner d'articles.
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              // Pour les autres cas (offre déjà soumise, acheteur, etc.), afficher le tableau en lecture seule
              formattedArticles.length > 0 ? (
                <div className="articles-table-container">
                  {/* NOUVEAU: Message d'information pour prestataires en attente de décision */}
                  {isPrestataireView && operationContext?.status === 'En attente de décision de l\'acheteur' && (
                    <div className="info-message" style={{
                      marginBottom: '20px',
                      padding: '15px',
                      backgroundColor: '#e3f2fd',
                      borderLeft: '4px solid #2196f3',
                      borderRadius: '4px'
                    }}>
                      <p style={{ margin: 0, color: '#1976d2', fontWeight: 500 }}>
                        Votre offre a été envoyée à l'acheteur. Vous serez informé de sa décision.
                      </p>
                    </div>
                  )}

                  <div className="articles-table-wrapper">
                    <table>
                      <thead>
                        <tr>
                          <th>N° Réf</th>
                          <th>Description de la prestation</th>
                          <th>Unité</th>
                          <th>Quantité</th>
                          <th>Prix unitaire HT</th>
                          <th>Total HT</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formattedArticles.map((article: any, index: number) => (
                          <tr key={article.id || index}>
                            <td>{article.code}</td>
                            <td>
                              <div className="article-name">{article.nom}</div>
                            </td>
                            <td>{article.unite}</td>
                            <td>{article.quantite}</td>
                            <td>{formatPrice(article.prixUnitaire, 4)}</td>
                            <td>{formatPrice(article.totalHT, 4)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Section total */}
                  <div className="articles-actions">
                    <div className="total-section">
                      <p><strong>Total de l'offre (Rang {rang}) : {formatPrice(totalOffre, 4)} HT</strong></p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="no-articles-message">
                  <p>Aucun article défini pour cette offre.</p>
                </div>
              )
            )}
          </div >
        </section >

        {/* NOUVEAU: Section Contact et documents */}
        < section >
          <h3 className="section-header">Contact et documents</h3>
          <div className="section-content coordinates-section">
            <Coordonnees
              commande={isPrestataireView ? operationData : undefined}
              operation={isPrestataireView ? undefined : operationData}
              prestataire={isClientView ? operationData?.commandes?.[rang - 1] : undefined}
              isAcheteur={isClientView}
              isPresta={isPrestataireView}
              showSignataire={false}
            />
          </div>
        </section >

        {/* NOUVEAU: Section Numéro de référence interne - Visible uniquement pour l'acheteur en panier inversé */}
        {(() => {
          const isTransferred = operationData?.transferred === true;
          const statusNom = operationData?.status?.nom;
          const commandeActuelle = operationData?.commandes?.find((cmd: any) => cmd.rang === rang);
          const commandeActuelleRefuseeOuExpiree = commandeActuelle?.id_status === 8 || commandeActuelle?.id_status === 9;
          const commandeAvecProposition = commandeActuelle?.id_status === 15;
          const currentReference = operationData?.reference || operationData?.formulaire?.referenceInterne || '';

          // Afficher uniquement pour l'acheteur en panier inversé avec statut "À accepter" et proposition reçue
          const shouldShowReferenceField = isClientView && isTransferred && statusNom === 'À accepter' && !commandeActuelleRefuseeOuExpiree && commandeAvecProposition;

          if (shouldShowReferenceField) {
            return (
              <section className="reference-interne-section">
                <h3 className="section-header">Numéro de référence interne</h3>
                <div className="section-content" style={{ padding: '20px' }}>
                  <p style={{ margin: '0 0 12px 0', color: '#666', fontSize: '14px' }}>
                    Vous pouvez renseigner un numéro de référence interne (ex: numéro d'engagement comptable) avant d'accepter l'offre. Ce champ est facultatif.
                  </p>
                  <div className="form-group" style={{ maxWidth: '400px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px' }}>
                      N° de référence interne (facultatif)
                    </label>
                    <input
                      type="text"
                      value={referenceInterne}
                      onChange={(e) => setReferenceInterne(e.target.value)}
                      placeholder="Exemple : numéro d'engagement comptable"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                      disabled={updatingReference}
                    />
                  </div>
                </div>
              </section>
            );
          }
          return null;
        })()}

        {/* CORRECTION: Section Bon de Commande - Visible pour tous quand accepté */}
        {
          offreState.showBonCommande && (
            <section className="bon-commande-section">
              <h3 className="section-header">Bon de Commande</h3>
              <div className="section-content">
                <div className="bon-commande-info">
                  <div className="bon-commande-message">
                    <p>Le bon de commande a été généré et est disponible au téléchargement.</p>

                    <p>
                      <strong>
                        {isPrestataireView ? (
                          // Message pour les prestataires
                          `Votre offre a été acceptée le ${operationData?.updated_at ?
                            new Date(operationData.updated_at).toLocaleDateString('fr-FR') + ' à ' +
                            new Date(operationData.updated_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                            : 'date non disponible'
                          }.`
                        ) : (
                          // Message pour les acheteurs (logique existante)
                          `Opération acceptée par ${operationData?.commandes?.find((cmd: any) => parseInt(cmd.rang) === rang)?.contrat?.entite?.nom ||
                          operationData?.prestataire?.nom ||
                          'le prestataire'
                          } le ${operationData?.commandes?.find((cmd: any) => parseInt(cmd.rang) === rang)?.updated_at ?
                            new Date(operationData.commandes.find((cmd: any) => parseInt(cmd.rang) === rang).updated_at).toLocaleDateString('fr-FR') + ' à ' +
                            new Date(operationData.commandes.find((cmd: any) => parseInt(cmd.rang) === rang).updated_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                            : 'date non disponible'
                          }.`
                        )}
                      </strong>
                    </p>
                  </div>

                  {/* Bouton de téléchargement */}
                  <div className="bon-commande-actions">
                    <Button
                      value="Télécharger le Bon de Commande"
                      variant="primary"
                      size="medium"
                      icon={<PdfIcon />}
                      onClick={handleDownloadBonCommande}
                    />
                  </div>
                </div>
              </div>
            </section>
          )
        }

        {/* NOUVEAU: Section message de refus ou expiration - Visible pour tous quand refusé ou expiré */}
        {
          offreState.showRefusMessage && offreState.refusInfo && (
            <section className="refus-message-section">
              <div className="section-content consultation-info">
                <p>
                  {(() => {
                    const date = new Date(offreState.refusInfo.date).toLocaleDateString('fr-FR') + ' à ' +
                      new Date(offreState.refusInfo.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

                    // Si expiré
                    if (offreState.refusInfo.isExpired) {
                      return isPrestataireView
                        ? `Votre offre a expiré le ${date}.`
                        : `Opération expirée le ${date} pour ${offreState.refusInfo.prestataireNom} de rang ${offreState.refusInfo.rang}`;
                    }

                    // Si refusé par l'acheteur
                    if (offreState.refusInfo.refusedBy === 'acheteur') {
                      return isPrestataireView
                        ? `Votre offre a été refusée par ${offreState.refusInfo.acheteurNom} le ${date}.`
                        : `Vous avez refusé cette offre le ${date}.`;
                    }

                    // Si refusé par le prestataire
                    return isPrestataireView
                      ? `Vous avez refusé cette offre le ${date}.`
                      : `Opération refusée le ${date} par ${offreState.refusInfo.prestataireNom} de rang ${offreState.refusInfo.rang}`;
                  })()}
                </p>
              </div>
            </section>
          )
        }

        {/* CAS 1: Mode normal - Acheteur en attente */}
        {
          !operationContext.isTransferred && isClientView && operationContext.isPending && (
            <div className="section-content consultation-info">
              <div className="info-header">
                <h4>En attente de l'acceptation du prestataire</h4>
                <p>Le prestataire de rang {rang} doit accepter ou refuser cette opération.</p>
                {operationData?.expire_at && (
                  <p><strong>Échéance dans :</strong> <CompteARebourd targetDate={operationData.expire_at} /></p>
                )}
              </div>
            </div>
          )
        }

        {/* RÉUTILISATION: Section boutons d'actions (même classe que ConsultationBC) */}
        <div className="action-buttons-wrapper">
          {/* CAS 1: Panier NORMAL - Prestataire accepte/refuse directement le BC */}
          {!isOperationExpired && !operationContext.isTransferred && isPrestataireView && operationContext.isPending && !operationContext.isRefused && !operationContext.isAccepted && (
            <div className="action-buttons-row">
              <Button
                value="Refuser l'opération"
                variant="secondary"
                size="small"
                onClick={() => setIsRefuseModalOpen(true)}
                disabled={loading}
              />
              <Button
                value="Accepter l'opération"
                variant="primary"
                size="small"
                onClick={handleAccepterAvecCGV}
                disabled={loading}
              />
            </div>
          )}

          {/* CAS 2: Panier INVERSÉ - Prestataire transfère son offre à l'acheteur */}
          {(() => {
            // CORRECTION: Lire transferred depuis operation pour les prestataires
            const isTransferred = isPrestataireView
              ? operationData?.operation?.transferred === true
              : operationData?.transferred === true;

            const statusNom = operationData?.status?.nom;
            const statusId = operationData?.status?.id;

            // Le prestataire doit voir les boutons uniquement si :
            // - Panier inversé (transferred: true)
            // - Status est "À chiffrer" (id: 4) ou similaire (pas "En attente de décision")
            const shouldShowButtonsForPrestataire = isPrestataireView &&
              isTransferred &&
              (statusId === 4 || statusNom === 'À chiffrer') &&
              !operationContext.isRefused &&
              !operationContext.isAccepted;

            if (!isOperationExpired && shouldShowButtonsForPrestataire) {
              return (
                <div className="action-buttons-row">
                  <Button
                    value="Refuser de faire une offre"
                    variant="secondary"
                    size="small"
                    onClick={() => setIsRefuseModalOpen(true)}
                    disabled={loading}
                  />
                  <Button
                    value="Envoyer mon offre à l'acheteur"
                    variant="primary"
                    size="small"
                    onClick={() => setIsEnvoyerOffreModalOpen(true)}
                    disabled={loading || existingArticles.length === 0}
                  />
                </div>
              );
            }

            return null;
          })()}

          {/* CAS 2: Acheteur en attente de décision */}
          {(() => {
            // NOUVEAU: Logique pour gérer panier normal (transferred: false) et panier inversé (transferred: true)
            const statusNom = operationData?.status?.nom;
            const isTransferred = operationData?.transferred === true;
            const isAdminOrResponsable = isSignataireRole(user?.role?.toString() ?? "");

            // NOUVEAU: Vérifier si c'est un rang avec refus ou expiration précédent (panier normal)
            const commandeActuelle = operationData?.commandes?.find((cmd: any) => cmd.rang === rang);
            const rangPrecedent = rang - 1;
            const commandeRangPrecedent = rangPrecedent > 0
              ? operationData?.commandes?.find((cmd: any) => cmd.rang === rangPrecedent)
              : null;
            // CORRECTION: Inclure les commandes expirées (id_status === 9) en plus des refusées (id_status === 8)
            const rangPrecedentRefuse = commandeRangPrecedent?.id_status === 8 || commandeRangPrecedent?.id_status === 9; // Refusé ou Expiré

            console.log('=== DEBUG AFFICHAGE BOUTONS OFFRE BC ===');
            console.log('Status:', statusNom);
            console.log('Is transferred:', isTransferred);
            console.log('Rang actuel:', rang);
            console.log('Rang précédent refusé:', rangPrecedentRefuse);
            console.log('Commande actuelle:', commandeActuelle);
            console.log('Is admin/responsable:', isAdminOrResponsable);

            // CORRECTION: L'acheteur ne doit avoir les boutons que dans ces cas précis :
            // 1. Panier inversé ET status "À accepter" ET commande pas refusée ET offre disponible (id_status = 15)
            // 2. Panier normal ET status "À analyser" ET rang 1 ET commande pas refusée/expirée ET offre reçue
            // 3. Panier normal ET status "À analyser" ET rang > 1 ET rang précédent refusé/expiré ET commande actuelle consultée ET pas encore refusée/expirée

            // Vérifier si la commande actuelle est refusée ou expirée (pas d'offre à traiter)
            const commandeActuelleRefuseeOuExpiree = commandeActuelle?.id_status === 8 || commandeActuelle?.id_status === 9;

            // Vérifier si le prestataire a envoyé une offre (articles présents ou status approprié)
            const prestataireAEnvoyeOffre = commandeActuelle && (
              (commandeActuelle.articles && commandeActuelle.articles.length > 0) || // A des articles
              commandeActuelle.id_status === 13 || // En attente de décision
              commandeActuelle.id_status === 4 // À accepter
            );

            // CORRECTION: Pour panier inversé, vérifier aussi que la commande a une proposition (id_status = 15)
            const commandeAvecProposition = commandeActuelle?.id_status === 15;

            // Pour le rang 1 : afficher les boutons SAUF si la commande est déjà refusée/expirée ET si une offre a été reçue
            // Pour les rangs 2/3 : afficher les boutons SI rang précédent refusé/expiré ET consultation lancée pour ce rang ET offre reçue ET pas encore refusée/expirée
            const shouldShowButtonsForClient = isClientView && (
              (isTransferred && statusNom === 'À accepter' && !commandeActuelleRefuseeOuExpiree && commandeAvecProposition) || // Panier inversé avec proposition et pas refusée/expirée
              (!isTransferred && statusNom === 'À analyser' && rang === 1 && !commandeActuelleRefuseeOuExpiree && prestataireAEnvoyeOffre) || // Panier normal rang 1 avec offre non refusée/expirée
              (!isTransferred && statusNom === 'À analyser' && rang > 1 && rangPrecedentRefuse && commandeActuelle && commandeActuelle.id_status !== 1 && !commandeActuelleRefuseeOuExpiree && prestataireAEnvoyeOffre) // Panier normal rang 2/3 avec précédent refusé/expiré, consultation lancée, offre reçue et pas refusée/expirée
            );

            console.log('Should show buttons for client:', shouldShowButtonsForClient);

            // CORRECTION: Afficher les boutons pour l'acheteur
            if (!isOperationExpired && shouldShowButtonsForClient && isAdminOrResponsable) {
              return (
                <div className="action-buttons-row">
                  <Button
                    value="Refuser l'offre"
                    variant="secondary"
                    size="small"
                    onClick={() => setIsRefuseOffreModalOpen(true)}
                    disabled={loading}
                  />
                  <Button
                    value="Accepter l'offre"
                    variant="primary"
                    size="small"
                    onClick={handleAccepterOffreAvecCGV}
                    disabled={loading || (!acceptCGV && !cgvAlreadyAccepted)}
                  />
                </div>
              );
            } else if (!isOperationExpired && shouldShowButtonsForClient) {
              // CORRECTION: Acheteur simple (non signataire) - peut refuser mais pas accepter
              return (
                <>
                  <div className="action-buttons-row">
                    <Button
                      value="Refuser l'offre"
                      variant="secondary"
                      size="small"
                      onClick={() => setIsRefuseOffreModalOpen(true)}
                      disabled={loading}
                    />
                    <Button
                      value="Obtenir la validation de l'acheteur signataire"
                      variant="primary"
                      size="small"
                      onClick={handleNotifyResponsable}
                    />
                  </div>
                  {notificationInfo.sent && notificationInfo.labels.length > 0 && (
                    <div className="notifications-sent-list">
                      {notificationInfo.labels.map((label: string, index: number) => (
                        <div key={index} className="notification-sent-info">
                          <span className="notification-sent-icon">✓</span>
                          <span>{label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            }

            return null;
          })()}

          {/* NOUVEAU: Messages de statut final */}
          {operationContext.isAccepted && (
            <div className="status-message accepted">
              ✓ Offre acceptée
            </div>
          )}

          {operationContext.isRefused && (
            <div className="status-message refused">
              ✗ Offre refusée
            </div>
          )}

          {operationContext.isExpired && (
            <div className="status-message expired">
              ⏰ Offre expirée
            </div>
          )}

          {/* Bouton PDF toujours visible et aligné avec les autres boutons */}
          <div className="print-button-inline">
            <PrintButton
              targetRef={printRef}
              fileName={`${commande?.numero || commande?.operation?.numero} - Offre`}
              value="PDF"
              suiviHeaderProps={{
                titre: commande.operation?.nom || commande?.nom,
                sousTitre: getSousTitre(),
                subtitle: getSubtitle(),
                status: getDisplayStatus(),
                statusColor: getStatusColor(getDisplayStatus()),
                delais: commande.expire_at,
                backgroundImage: getLotImage(commande?.operation?.lot_technique?.nom || commande?.lot_technique?.nom),
              }}
            />
          </div>
        </div>

        {/* Modals */}
        <ValidationPopUp
          isOpen={isAcceptModalOpen}
          title="Accepter l'opération"
          text={`Êtes-vous sûr de vouloir accepter cette opération pour un montant de ${formatPrice(totalOffre, 4)} HT ?`}
          onClose={() => setIsAcceptModalOpen(false)}
          onConfirm={async () => {
            setIsAcceptModalOpen(false);
            await handleAcceptOperation();
          }}
          cancelLabel="Annuler"
          confirmLabel="Accepter"
        />

        <ValidationPopUp
          isOpen={isRefuseModalOpen}
          title="Refuser l'opération"
          text={`Êtes-vous sûr de vouloir refuser cette opération ?`}
          onClose={() => setIsRefuseModalOpen(false)}
          onConfirm={async () => {
            setIsRefuseModalOpen(false);
            await handleRefuseOperation();
          }}
          cancelLabel="Annuler"
          confirmLabel="Refuser"
        />

        {/* CORRECTION: Modal CGV avec fermeture automatique */}
        <CGVAccept
          acceptCGV={acceptCGV}
          setAcceptCGV={setAcceptCGV}
          isOpen={isCGVModalOpen}
          onClose={() => {
            setIsCGVModalOpen(false);
            setReadyToAcceptOperationAfterCGV(false);
            setReadyToAcceptOffreAfterCGV(false);
          }}
          onConfirm={() => {
            console.log("=== CGV CONFIRMÉES ===");

            // ✅ Forcer la mise à jour avant de continuer
            setAcceptCGV(true);

            setTimeout(() => {
              setIsCGVModalOpen(false);

              if (readyToAcceptOperationAfterCGV) {
                setReadyToAcceptOperationAfterCGV(false);
                setIsAcceptModalOpen(true);
              }

              if (readyToAcceptOffreAfterCGV) {
                setReadyToAcceptOffreAfterCGV(false);
                setIsAcceptOffreModalOpen(true);
              }
            }, 100);
          }}
        />

        {/* NOUVEAU: Modals pour acceptation/refus offre (acheteurs) */}
        {/* Modal reference supprimée - début */}
        {false && isReferenceModalOpen && (
          <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div className="modal-content" style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}>
              <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600 }}>
                Numéro de référence interne
              </h2>
              <p style={{ margin: '0 0 16px 0', color: '#666', fontSize: '14px' }}>
                Le numéro de référence interne n'est pas renseigné. Souhaitez-vous en ajouter un avant d'accepter l'offre ?
              </p>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px' }}>
                  Numéro de référence interne (facultatif)
                </label>
                <input
                  type="text"
                  value={referenceInterne}
                  onChange={(e) => setReferenceInterne(e.target.value)}
                  placeholder="Exemple : numéro d’engagement comptable"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  disabled={updatingReference}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  onClick={handleSkipReference}
                  disabled={updatingReference}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    cursor: updatingReference ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: updatingReference ? 0.6 : 1
                  }}
                >
                  Non, continuer sans
                </button>
                <button
                  onClick={handleUpdateReferenceAndAccept}
                  disabled={updatingReference || !referenceInterne.trim()}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '6px',
                    backgroundColor: '#da7027',
                    color: 'white',
                    cursor: (updatingReference || !referenceInterne.trim()) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 500,
                    opacity: (updatingReference || !referenceInterne.trim()) ? 0.6 : 1
                  }}
                >
                  {updatingReference ? 'Mise à jour...' : 'Valider et continuer'}
                </button>
              </div>
            </div>
          </div>
        )}

        <ValidationPopUp
          isOpen={isAcceptOffreModalOpen}
          title="Accepter l'offre"
          text={`Êtes-vous sûr de vouloir accepter cette offre pour un montant de ${formatPrice(totalOffre, 4)} HT ? Cette action est définitive.`}
          onClose={() => setIsAcceptOffreModalOpen(false)}
          onConfirm={async () => {
            setIsAcceptOffreModalOpen(false);
            await handleAcceptOffre();
          }}
          cancelLabel="Annuler"
          confirmLabel="Accepter l'offre"
        />

        <ValidationPopUp
          isOpen={isRefuseOffreModalOpen}
          title="Refuser l'offre"
          text={`Êtes-vous sûr de vouloir refuser cette offre ?`}
          onClose={() => setIsRefuseOffreModalOpen(false)}
          onConfirm={async () => {
            setIsRefuseOffreModalOpen(false);
            await handleRefuseOffre();
          }}
          cancelLabel="Annuler"
          confirmLabel="Refuser l'offre"
        />

        {/* NOUVEAU: Modal pour envoyer l'offre (prestataires panier inversé) */}
        <ValidationPopUp
          isOpen={isEnvoyerOffreModalOpen}
          title="Envoyer votre offre à l'acheteur"
          text={`Êtes-vous sûr de vouloir envoyer votre offre d'un montant de ${formatPrice(existingArticles.reduce((sum, article) => sum + article.totalHT, 0), 4)} HT à l'acheteur pour validation ? L'acheteur devra ensuite valider votre proposition.`}
          onClose={() => setIsEnvoyerOffreModalOpen(false)}
          onConfirm={async () => {
            setIsEnvoyerOffreModalOpen(false);
            await handleEnvoyerOffre();
          }}
          cancelLabel="Annuler"
          confirmLabel="Envoyer l'offre"
        />

        {/* NOUVEAU: Modal pour sélectionner les articles du catalogue */}
        <ModalArticles
          isOpen={isArticlesModalOpen}
          onClose={() => setIsArticlesModalOpen(false)}
          onAddToCommand={handleAddToCommand}
          commandes={operationData?.commandes || [operationData]}
          existingSelection={existingArticles}
          lotNom={operationData?.operation?.lot_technique?.nom || operationData?.lot_technique?.nom || 'Lot technique'}
          prestataireRang={rang}
        />

        {/* Modal notification responsable */}
        <ValidationPopUp
          isOpen={isNotifyResponsableModalOpen}
          title="Obtenir la validation de l'acheteur signataire"
          text=""
          onClose={() => {
            setIsNotifyResponsableModalOpen(false);
            setSelectedResponsable(null);
          }}
          onConfirm={handleConfirmNotifyResponsable}
          cancelLabel="Annuler"
          confirmLabel="Envoyer la demande"
        >
          <div className="mt-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sélectionnez le responsable à qui envoyer la demande :
            </label>
            {responsablesLoading ? (
              <p className="text-gray-500">Chargement...</p>
            ) : responsables.length === 0 ? (
              <p className="text-red-500">Aucun responsable disponible</p>
            ) : (
              <select
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={selectedResponsable ? `${selectedResponsable.type}-${selectedResponsable.id}` : ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    const [type, id] = value.split('-');
                    setSelectedResponsable({ id: parseInt(id), type });
                  } else {
                    setSelectedResponsable(null);
                  }
                }}
              >
                <option value="">-- Choisir un responsable --</option>
                {responsables.map((resp: any) => (
                  <option
                    key={`${resp.type}-${resp.id}`}
                    value={`${resp.type}-${resp.id}`}
                  >
                    {resp.prenom} {resp.nom} - {resp.type === 'responsable_achat' ? 'Acheteur signataire' : 'Validateur Achat'}{resp.fonction ? ` (${resp.fonction})` : ''}
                  </option>
                ))}
              </select>
            )}
            {selectedResponsable && (
              <p className="mt-2 text-sm text-gray-600">
                Email : {responsables.find((r: any) => r.id === selectedResponsable.id && r.type === selectedResponsable.type)?.email_contact}
              </p>
            )}
          </div>
        </ValidationPopUp>

      </div >
    </>
  );
};

export default OffreBC;